import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  setDrawerOpen,
  setActiveTab,
  selectNavigationData,
  selectTopAppBar,
} from 'store/navigationSlice';
import { getTabURL } from 'lib/router';
import { isNil } from 'swipeclock-frontend';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { ReactComponent as WFHLogo } from 'assets/WorkforceHub_Logo_Icon_Light.svg';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import useMediaQuery from '@mui/material/useMediaQuery';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import SvgIcon from '@mui/material/SvgIcon';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import UserAvatar from 'components/UserAvatar';

function TopAppBar({ showLogo = true, showTitle = true }) {
  const tabletRef = useRef(null);
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const {
    drawerIsOpen,
    topAppBar,
    hasDrawer,
    productLevel,
    disabledTopAppBar,
  } = useSelector(selectNavigationData);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const showHamburger = isMobile && hasDrawer;

  const handleNavigation = (_, newValue) => {
    // Set active tab based on the tab value which is the order
    const tab = findTabFromOrderValue(newValue, topAppBar);
    if (tab) {
      dispatch(setActiveTab(newValue));
      return navigate(getTabURL(tab));
    }

    console.error(
      'Error: Could not find tab for order value',
      newValue,
      topAppBar
    );
    return null;
    // throw new Error("Couldn't find tab for order value", newValue, topAppBar);
  };

  function handleOnHamburgerClick() {
    dispatch(setDrawerOpen({ open: !drawerIsOpen }));
  }

  const title = productLevel?.displayName
    ? productLevel.displayName
    : 'WorkforceHub Time';

  return (
    <>
      <StyledAppBar>
        <StyledToolbar disableGutters>
          {showHamburger && !disabledTopAppBar && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleOnHamburgerClick}
            >
              <MenuIcon />
            </IconButton>
          )}
          {showLogo && (
            <SvgIcon component={WFHLogo} viewBox="0 0 127.71 111.52" />
          )}

          <ButtonBase
            onClick={() => {
              navigate(getTabURL(topAppBar[0]));
              dispatch(setActiveTab(0));
            }}
          >
            <PageTitle>{showTitle && title}</PageTitle>
          </ButtonBase>
          <CenterContent>
            <Navigation
              disabled={disabledTopAppBar}
              onNavigate={handleNavigation}
            />
          </CenterContent>
          <TrailingContent>
            <UserAvatar />
          </TrailingContent>
        </StyledToolbar>
        <TabletNavigation ref={tabletRef}>
          <Navigation
            disabled={disabledTopAppBar}
            onNavigate={handleNavigation}
          />
        </TabletNavigation>
      </StyledAppBar>
      {/* The TopAppBar is positioned as 'fixed', so the following element */}
      {/* provides a space to prevent content from being obscured by it. */}
      <ToolbarSpacer tabletRef={tabletRef.current} />
    </>
  );
}

TopAppBar.propTypes = {
  showLogo: PropTypes.bool,
  showTitle: PropTypes.bool,
};

export default TopAppBar;

function Navigation({ disabled: isDisabled, onNavigate: handleNavigation }) {
  const { activeTabIndex, topAppBar } = useSelector(selectTopAppBar);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Guard against bad data
  if (
    isNil(topAppBar?.[0]?.order) ||
    !checkActiveTabIndex(topAppBar, activeTabIndex)
  ) {
    return <StyledTabs />;
  }

  return (
    <StyledTabs
      value={activeTabIndex}
      onChange={handleNavigation}
      scrollButtons="auto"
      variant={isMobile ? 'fullWidth' : 'standard'}
      disabled={isDisabled}
    >
      {topAppBar.map((item) => (
        <Tab
          disabled={isDisabled && item.order !== activeTabIndex}
          label={item.title}
          key={item.title}
          value={item.order}
        />
      ))}
    </StyledTabs>
  );
}

Navigation.propTypes = {
  onNavigate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// ##############################
// ### Helper Functions
// ##############################

const findTabFromOrderValue = (value, topAppBar) => {
  // Find the matching topAppBar item
  const tab = topAppBar?.find((item) => item.order === value);

  return tab;
};

function checkActiveTabIndex(topAppBar, activeTabIndex) {
  // eslint-disable-next-line no-restricted-syntax
  for (const item of topAppBar) {
    if (item.order === activeTabIndex) {
      return true;
    }
  }
  return false;
}

// ##############################
// ### Styled Components
// ##############################

export const StyledAppBar = styled((props) => (
  <AppBar position="fixed" {...props} />
))(({ theme }) => ({
  backgroundColor: theme.palette.custom.navyBlue.main,
  color: theme.palette.primary.contrastText,
  [theme.breakpoints.up('md')]: {
    zIndex: theme.zIndex.drawer + 1,
  },
}));

export const StyledToolbar = styled((props) => (
  <Toolbar disableGutters {...props} />
))(({ theme }) => ({
  backgroundColor: theme.palette.custom.navyBlue.main,
  color: theme.palette.primary.contrastText,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  gap: theme.spacing(1),
}));

export const ToolbarSpacer = styled(
  (props) => <Toolbar disableGutters {...props} />,
  {
    shouldForwardProp: (prop) => prop !== 'tabletRef',
  }
)(({ theme, tabletRef }) => ({
  marginTop: `${tabletRef?.offsetHeight || 0}px`,
  [theme.breakpoints.down('md')]: {
    marginTop: `${tabletRef?.offsetHeight || 48}px`, // There is weird cases when a render doesn't happen. This is a hack to fix it.
  },
}));

export const PageTitle = styled((props) => (
  <Typography variant="h6" {...props} />
))(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  cursor: 'pointer',
  '&:hover': {
    color: theme.palette.secondary.light,
  },
  [theme.breakpoints.down('md')]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export const CenterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexGrow: 1,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

export const TrailingContent = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexGrow: 1,
  },
}));

export const TabletNavigation = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexGrow: 1,
  width: '100%',
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

export const StyledTabs = styled((props) => <Tabs {...props} />, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
  '& .MuiTabs-indicator': {
    color: theme.palette.secondary.light,
  },
  '& .MuiTab-root': {
    textTransform: 'UPPERCASE',
    fontWeight: theme.typography.fontWeightRegular,
    color: theme.palette.primary.contrastText,
    '&.Mui-selected': {
      color: theme.palette.secondary.light,
    },
    '&.Mui-disabled': {
      color: alpha(theme.palette.grey[50], 0.4),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.75rem',
      minWidth: '64px',
    },
  },
  '& .MuiTab-root:hover': {
    color: theme.palette.secondary.light,
  },
}));
